import { copy } from '@ohear/shared'
import { Button, Card, EmptyState, Input, Spinner } from '@ohear/ui'
import { createFileRoute } from '@tanstack/react-router'
/**
 * Admin client-error dashboard.
 *
 * Reads aggregate counters from the `analytics-query` Cloudflare Worker
 * (Workers Analytics Engine over the `client_errors` dataset). This is
 * intentionally separate from /admin/client-errors which renders the
 * triage queue from Postgres — this surface is for at-a-glance volume,
 * not for resolving individual rows.
 *
 * TODO i18n: this page is admin-internal and English-only for now.
 */
import { useState } from 'react'
import { AdminGuard } from '../../features/admin/AdminGuard'
import { AdminNav } from '../../features/admin/AdminNav'
import { type ClientErrorRange, useClientErrorStats } from '../../lib/api/admin-health'
import { useT } from '../../lib/i18n/use-t'

export const Route = createFileRoute('/admin/health')({
  component: HealthPage,
})

const RANGE_LABEL: Record<ClientErrorRange, string> = {
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
}

// Distinct, accessible colors for the donut. Pick a small palette that
// stays readable on both light and dark backgrounds.
const DONUT_COLORS = [
  '#3b82f6', // blue
  '#ef4444', // red
  '#10b981', // green
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f97316', // orange
] as const

function HealthPage() {
  const t = useT()
  const [range, setRange] = useState<ClientErrorRange>('24h')
  const [orgDraft, setOrgDraft] = useState('')
  const [orgId, setOrgId] = useState<string | undefined>(undefined)

  const stats = useClientErrorStats({ range, orgId })

  const onApplyOrg = (e: React.FormEvent) => {
    e.preventDefault()
    setOrgId(orgDraft.trim() || undefined)
  }

  const onClearOrg = () => {
    setOrgDraft('')
    setOrgId(undefined)
  }

  const data = stats.data

  return (
    <AdminGuard>
      <section className="flex flex-col gap-3" aria-labelledby="admin-health-title">
        <header className="flex flex-col gap-1">
          <div className="text-[12px] uppercase tracking-wide text-[var(--text-3)]">
            Platform admin
          </div>
          <h1 id="admin-health-title" className="text-[22px] font-semibold tracking-tight m-0">
            Client error dashboard
          </h1>
          <p className="text-[13px] text-[var(--text-2)] m-0">
            Aggregate browser-side error volume from Workers Analytics Engine. Use{' '}
            <code className="text-[12px]">/admin/client-errors</code> for the row-level triage
            queue.
          </p>
        </header>

        <AdminNav />

        <Card>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex gap-1" role="tablist" aria-label="Time range">
              {(['24h', '7d', '30d'] as const).map((r) => (
                <Button
                  key={r}
                  size="sm"
                  variant={r === range ? 'primary' : 'ghost'}
                  onClick={() => setRange(r)}
                  aria-pressed={r === range}
                >
                  {RANGE_LABEL[r]}
                </Button>
              ))}
            </div>
            <form className="flex items-end gap-2" onSubmit={onApplyOrg}>
              <label className="flex flex-col gap-1 text-[13px]">
                <span className="font-medium">{t(copy.admin.FILTERS.filter_by_org)}</span>
                <Input
                  value={orgDraft}
                  onChange={(e) => setOrgDraft(e.target.value)}
                  placeholder="Org UUID"
                  className="min-w-[260px]"
                />
              </label>
              <Button type="submit" size="sm">
                Apply
              </Button>
              {orgId && (
                <Button type="button" size="sm" variant="ghost" onClick={onClearOrg}>
                  Clear
                </Button>
              )}
            </form>
          </div>
        </Card>

        {stats.isLoading ? (
          <Card>
            <div className="flex items-center gap-2 text-[var(--text-2)]">
              <Spinner /> Loading analytics...
            </div>
          </Card>
        ) : stats.isError ? (
          <Card>
            <p className="m-0 text-[13px] text-[var(--text-2)]">
              Could not reach the analytics worker. Try again in a moment.
            </p>
          </Card>
        ) : !data ? null : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Card>
                <div className="text-[11px] uppercase tracking-wide text-[var(--text-3)]">
                  Total errors · {RANGE_LABEL[range]}
                </div>
                <div className="text-[28px] font-semibold tabular-nums mt-1">
                  {data.total.toLocaleString()}
                </div>
                <div className="text-[11px] text-[var(--text-3)] mt-1">
                  Reported {new Date(data.generated_at).toLocaleTimeString()}
                </div>
              </Card>
              <Card>
                <div className="text-[11px] uppercase tracking-wide text-[var(--text-3)]">
                  Distinct error types
                </div>
                <div className="text-[28px] font-semibold tabular-nums mt-1">
                  {data.by_type.length}
                </div>
              </Card>
              <Card>
                <div className="text-[11px] uppercase tracking-wide text-[var(--text-3)]">
                  Orgs affected
                </div>
                <div className="text-[28px] font-semibold tabular-nums mt-1">
                  {data.by_org.length}
                </div>
              </Card>
            </div>

            <Card title="Error type distribution">
              {data.by_type.length === 0 ? (
                <EmptyState title="No errors in this window" />
              ) : (
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  <DonutChart buckets={data.by_type} />
                  <ul className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-1 text-[13px] m-0 p-0 list-none">
                    {data.by_type.map((b, i) => (
                      <li key={b.type} className="flex items-center gap-2">
                        <span
                          aria-hidden="true"
                          className="inline-block w-3 h-3 rounded-sm"
                          style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }}
                        />
                        <span className="font-medium">{b.type}</span>
                        <span className="ml-auto tabular-nums text-[var(--text-2)]">
                          {b.count.toLocaleString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>

            <Card title="Top error messages" dense>
              {data.top_messages.length === 0 ? (
                <EmptyState title="No messages in this window" />
              ) : (
                <table
                  className="w-full text-[13px] border-separate"
                  style={{ borderSpacing: 0 }}
                  aria-label="Top error messages"
                >
                  <thead className="text-left text-[11px] uppercase tracking-wide text-[var(--text-2)]">
                    <tr>
                      <th className="px-3 py-2 font-medium">Rank</th>
                      <th className="px-3 py-2 font-medium">Message</th>
                      <th className="px-3 py-2 font-medium text-right">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_messages.map((m, i) => (
                      <tr key={`${i}-${m.message}`} className="align-top">
                        <td
                          className="px-3 py-2 tabular-nums text-[var(--text-2)] border-t"
                          style={{ borderColor: 'var(--border)' }}
                        >
                          {i + 1}
                        </td>
                        <td
                          className="px-3 py-2 border-t font-mono text-[12px] break-all"
                          style={{ borderColor: 'var(--border)' }}
                        >
                          {m.message}
                        </td>
                        <td
                          className="px-3 py-2 text-right tabular-nums border-t"
                          style={{ borderColor: 'var(--border)' }}
                        >
                          {m.count.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>

            <Card title="Per-organization breakdown" dense>
              {data.by_org.length === 0 ? (
                <EmptyState title="No org-attributed errors" />
              ) : (
                <table
                  className="w-full text-[13px] border-separate"
                  style={{ borderSpacing: 0 }}
                  aria-label="Errors per organization"
                >
                  <thead className="text-left text-[11px] uppercase tracking-wide text-[var(--text-2)]">
                    <tr>
                      <th className="px-3 py-2 font-medium">Org ID</th>
                      <th className="px-3 py-2 font-medium text-right">Count</th>
                      <th className="px-3 py-2 font-medium text-right">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_org.map((o) => {
                      const share = data.total > 0 ? (o.count / data.total) * 100 : 0
                      return (
                        <tr key={o.org_id}>
                          <td
                            className="px-3 py-2 font-mono text-[12px] border-t"
                            style={{ borderColor: 'var(--border)' }}
                          >
                            {o.org_id}
                          </td>
                          <td
                            className="px-3 py-2 text-right tabular-nums border-t"
                            style={{ borderColor: 'var(--border)' }}
                          >
                            {o.count.toLocaleString()}
                          </td>
                          <td
                            className="px-3 py-2 text-right tabular-nums text-[var(--text-2)] border-t"
                            style={{ borderColor: 'var(--border)' }}
                          >
                            {share.toFixed(1)}%
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </Card>
          </>
        )}
      </section>
    </AdminGuard>
  )
}

interface DonutChartProps {
  buckets: ReadonlyArray<{ type: string; count: number }>
  size?: number
  thickness?: number
}

/**
 * Inline-SVG donut chart. No chart library — this is a single page with one
 * pie, and the math is six lines. Slices render as stroked arcs around a
 * circle so we don't need to compute paths for each wedge.
 */
function DonutChart({ buckets, size = 180, thickness = 28 }: DonutChartProps) {
  const total = buckets.reduce((s, b) => s + b.count, 0)
  if (total === 0) return null
  const r = (size - thickness) / 2
  const cx = size / 2
  const cy = size / 2
  const circumference = 2 * Math.PI * r

  let cursor = 0
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label="Donut chart"
    >
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--bg-3)" strokeWidth={thickness} />
      {buckets.map((b, i) => {
        const frac = b.count / total
        const dash = frac * circumference
        const offset = -cursor
        cursor += dash
        return (
          <circle
            key={b.type}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={DONUT_COLORS[i % DONUT_COLORS.length]}
            strokeWidth={thickness}
            strokeDasharray={`${dash} ${circumference - dash}`}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${cx} ${cy})`}
          >
            <title>{`${b.type}: ${b.count}`}</title>
          </circle>
        )
      })}
      <text
        x={cx}
        y={cy}
        textAnchor="middle"
        dominantBaseline="central"
        className="fill-[var(--text)]"
        style={{ fontSize: '14px', fontWeight: 600 }}
      >
        {total.toLocaleString()}
      </text>
    </svg>
  )
}
